import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Eye,
  Users,
  MousePointerClick,
  TrendingUp,
  Activity,
  Globe2,
  Smartphone,
  Monitor,
  Compass,
  Radio,
  UserPlus,
  UserCheck,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  CartesianGrid,
} from "recharts";
import client from "@/api/client";

const RANGES = [
  { id: "24h", label: "24h" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "90d", label: "90 days" },
];

function Metric({ icon: Icon, label, value, hint, testId, onClick }) {
  return (
    <Card
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter") onClick(); } : undefined}
      className={`rounded-2xl border border-border p-4 sm:p-5 ${onClick ? "cursor-pointer transition-all hover:border-primary/50 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40" : ""}`}>
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className="h-4 w-4" />
        <span className="text-xs font-medium uppercase tracking-widest">{label}</span>
      </div>
      <p className="font-display text-2xl sm:text-3xl font-semibold mt-2 sm:mt-3 tabular-nums" data-testid={testId || `metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>{value}</p>
      {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
    </Card>
  );
}

function TopList({ title, rows, icon: Icon }) {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  return (
    <Card className="rounded-2xl border border-border p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-semibold text-sm">{title}</h3>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">—</p>
      ) : (
        <ul className="space-y-2">
          {rows.slice(0, 8).map((r) => {
            const pct = (r.count / total) * 100;
            return (
              <li key={r.label} className="relative overflow-hidden rounded-lg">
                <div className="absolute inset-y-0 start-0 bg-primary/10" style={{ width: `${pct}%` }} />
                <div className="relative flex items-center justify-between px-2.5 py-1.5 text-sm">
                  <span className="truncate max-w-[70%]">{r.label}</span>
                  <span className="font-mono text-xs text-muted-foreground tabular-nums">{r.count}</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

function fmt(n) {
  if (typeof n !== "number") return "0";
  if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "k";
  return String(n);
}

export function AdminAnalytics() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [top, setTop] = useState(null);
  const [series, setSeries] = useState([]);
  const [realtime, setRealtime] = useState({ active_visitors: 0, top_active_pages: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, top_, ts] = await Promise.all([
        client.get("/admin/analytics/summary", { params: { rng: range } }),
        client.get("/admin/analytics/top", { params: { rng: range } }),
        client.get("/admin/analytics/timeseries", { params: { rng: range } }),
      ]);
      setSummary(s.data);
      setTop(top_.data);
      setSeries(ts.data.points || []);
    } catch (e) {
      console.error("Analytics load failed:", e);
    } finally {
      setLoading(false);
    }
  }, [range]);

  const loadRealtime = useCallback(async () => {
    try {
      const r = await client.get("/admin/analytics/realtime");
      setRealtime(r.data || { active_visitors: 0, top_active_pages: [] });
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadRealtime();
    const iv = setInterval(loadRealtime, 20000);
    return () => clearInterval(iv);
  }, [loadRealtime]);

  const chartData = useMemo(
    () => series.map((p) => ({ t: p.t.slice(5), pageviews: p.pageviews, visitors: p.visitors })),
    [series],
  );

  return (
    <div className="space-y-6" data-testid="admin-panel-analytics">
      <Card className="rounded-2xl border border-border p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="font-display text-xl font-semibold">{t("admin.analytics")}</h2>
            <p className="text-sm text-muted-foreground mt-1">{t("admin.analyticsDesc")}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="realtime-active">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <Radio className="h-3 w-3" /> {realtime.active_visitors} {t("admin.activeNow")}
            </span>
            <Tabs value={range} onValueChange={setRange}>
              <TabsList className="rounded-xl h-9">
                {RANGES.map((r) => (
                  <TabsTrigger key={r.id} value={r.id} className="rounded-lg text-xs" data-testid={`analytics-range-${r.id}`}>
                    {r.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
        </div>
      </Card>

      {loading || !summary ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Metric icon={Eye} label={t("admin.pageviews")} value={fmt(summary.pageviews)} testId="metric-pageviews" />
            <Metric icon={Users} label={t("admin.visitors")} value={fmt(summary.unique_visitors)} hint={`${fmt(summary.new_visitors)} ${t("admin.newVisitors")}`} testId="metric-visitors" />
            <Metric icon={Activity} label={t("admin.sessions")} value={fmt(summary.sessions)} hint={`${summary.pageviews_per_session}/sess`} testId="metric-sessions" />
            <Metric icon={TrendingUp} label={t("admin.bounceRate")} value={`${summary.bounce_rate}%`} testId="metric-bounce" />
            <Metric
              icon={UserPlus}
              label={t("admin.newUsers")}
              value={fmt(summary.users?.new_in_range ?? 0)}
              hint={`${fmt(summary.users?.total ?? 0)} ${t("admin.totalUsers")}`}
              testId="metric-new-users"
              onClick={() => navigate("/admin?section=users")}
            />
            <Metric
              icon={UserCheck}
              label={t("admin.awaitingApproval")}
              value={fmt(summary.users?.pending_approval ?? 0)}
              testId="metric-awaiting-approval"
              onClick={() => navigate("/admin?section=users&status=pending")}
            />
          </div>

          <Card className="rounded-2xl border border-border p-5">
            <h3 className="font-semibold text-sm mb-4">{t("admin.pageviewsOverTime")}</h3>
            {chartData.length === 0 ? (
              <p className="text-xs text-muted-foreground py-8 text-center">{t("admin.noDataYet")}</p>
            ) : (
              <div className="h-64" data-testid="analytics-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData} margin={{ top: 10, right: 12, bottom: 0, left: -12 }}>
                    <defs>
                      <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#29ABE2" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="#29ABE2" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.25} />
                    <XAxis dataKey="t" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                    <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <RTooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                    <Area type="monotone" dataKey="pageviews" stroke="#29ABE2" strokeWidth={2} fill="url(#pv)" />
                    <Area type="monotone" dataKey="visitors" stroke="#454F72" strokeWidth={1.5} fill="transparent" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <TopList title={t("admin.topPages")} rows={top?.top_pages || []} icon={Compass} />
            <TopList title={t("admin.topReferrers")} rows={top?.top_referrers || []} icon={TrendingUp} />
            <TopList title={t("admin.topCountries")} rows={top?.top_countries || []} icon={Globe2} />
            <TopList title={t("admin.topSources")} rows={top?.top_sources || []} icon={MousePointerClick} />
            <TopList title={t("admin.topDevices")} rows={top?.top_devices || []} icon={Smartphone} />
            <TopList title={t("admin.topBrowsers")} rows={top?.top_browsers || []} icon={Monitor} />
          </div>

          <Card className="rounded-2xl border border-border p-5">
            <h3 className="font-semibold text-sm mb-4">{t("admin.conversions")}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: "waitlist_submit", label: t("admin.waitlist") },
                { key: "contact_submit", label: t("admin.contactSubmissions") },
                { key: "booking_confirmed", label: t("admin.bookings") },
                { key: "event_rsvp", label: t("admin.rsvps") },
              ].map((c) => (
                <div key={c.key} className="rounded-xl border border-border p-4" data-testid={`conv-${c.key}`}>
                  <p className="text-xs text-muted-foreground">{c.label}</p>
                  <p className="font-display text-2xl font-semibold mt-1 tabular-nums">{summary.conversions[c.key] || 0}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-2xl border border-border p-5">
            <div className="flex items-center gap-2 mb-3">
              <Radio className="h-4 w-4 text-emerald-500" />
              <h3 className="font-semibold text-sm">{t("admin.liveActivity")}</h3>
              <Badge variant="outline" className="rounded-full text-[10px] gap-1">Last 5 min</Badge>
            </div>
            {realtime.top_active_pages.length === 0 ? (
              <p className="text-xs text-muted-foreground py-4 text-center">—</p>
            ) : (
              <ul className="space-y-1.5">
                {realtime.top_active_pages.map((r) => (
                  <li key={r.label} className="flex items-center justify-between text-sm">
                    <span className="truncate">{r.label || "(unknown)"}</span>
                    <span className="font-mono text-xs text-muted-foreground">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
